/**
 * Static benefit-group → tile configuration (design §7). This is the wizard's
 * shape: which groups exist, which are recommended, and which connector tiles
 * each group offers. Live tiles drive a form/flow; coming-soon tiles render
 * disabled.
 *
 * Backend wiring per group:
 *  - `db` tiles → `DbCredsForm` → validate-db + POST /api/connectors (the
 *    adapter UUID is resolved at runtime from GET /api/connectors by contract id).
 *  - `oauth` tiles → the existing managed-Airbyte OAuth initiate flow.
 *  - `kb` tiles → the knowledge sources API (default KB).
 *  - `spec` tiles → the API-spec import flow (WP-4).
 *  - `coming_soon` tiles → disabled.
 *
 * Group keys + the "required" set mirror the backend (`REQUIRED_GROUPS` in
 * onboarding.service.ts). Microcopy lives in i18n (`onboarding:groups.<key>`).
 */

import type { GroupKey } from "./api/onboarding";

export type TileKind = "kb" | "db" | "spec" | "oauth" | "coming_soon";

export interface TileConfig {
  /** Stable id for keys/test-ids; also the i18n label key under `tile.labels`. */
  id: string;
  /** Display label (not translated — vendor/product names are proper nouns). */
  label: string;
  kind: TileKind;
  /** For `oauth` tiles: the adapter contract id to resolve to a UUID at runtime. */
  contractId?: string;
}

export interface GroupConfig {
  key: GroupKey;
  /** Recommended/“required” marker — copy only; submit gating is server-side. */
  required: boolean;
  tiles: TileConfig[];
}

const DB_TILE: TileConfig = { id: "custom-db", label: "Custom database", kind: "db" };

export const ONBOARDING_GROUPS: GroupConfig[] = [
  {
    key: "catalog",
    required: true,
    tiles: [
      { id: "shopify", label: "Shopify", kind: "oauth", contractId: "shopify" },
      DB_TILE,
      { id: "woocommerce", label: "WooCommerce", kind: "coming_soon" },
      { id: "bigcommerce", label: "BigCommerce", kind: "coming_soon" },
      { id: "magento", label: "Magento", kind: "coming_soon" },
    ],
  },
  {
    key: "payments",
    required: true,
    tiles: [
      { id: "stripe", label: "Stripe", kind: "coming_soon" },
      { id: "razorpay", label: "Razorpay", kind: "coming_soon" },
      { id: "paypal", label: "PayPal", kind: "coming_soon" },
      { id: "braintree", label: "Braintree", kind: "coming_soon" },
    ],
  },
  {
    key: "orders",
    required: false,
    tiles: [
      { id: "shipstation", label: "ShipStation", kind: "coming_soon" },
      { id: "aftership", label: "AfterShip", kind: "coming_soon" },
    ],
  },
  {
    key: "crm",
    required: false,
    tiles: [
      DB_TILE,
      { id: "hubspot", label: "HubSpot", kind: "coming_soon" },
      { id: "salesforce", label: "Salesforce", kind: "coming_soon" },
    ],
  },
  {
    key: "knowledge",
    required: false,
    tiles: [
      { id: "help-center", label: "Help centre or docs", kind: "kb" },
      { id: "zendesk", label: "Zendesk", kind: "coming_soon" },
      { id: "intercom", label: "Intercom", kind: "coming_soon" },
      { id: "notion", label: "Notion", kind: "coming_soon" },
      { id: "drive", label: "Google Drive", kind: "coming_soon" },
    ],
  },
  {
    key: "api",
    required: false,
    tiles: [{ id: "openapi", label: "OpenAPI / Swagger / Postman", kind: "spec" }],
  },
  {
    key: "marketing",
    required: false,
    tiles: [
      { id: "klaviyo", label: "Klaviyo", kind: "coming_soon" },
      { id: "mailchimp", label: "Mailchimp", kind: "coming_soon" },
    ],
  },
];

export const ONBOARDING_GROUP_KEYS = ONBOARDING_GROUPS.map((g) => g.key);

/** The default/first step the wizard opens on. */
export const FIRST_GROUP: GroupConfig = ONBOARDING_GROUPS[0]!;

export function findGroup(key: string | null): GroupConfig {
  return ONBOARDING_GROUPS.find((g) => g.key === key) ?? FIRST_GROUP;
}
